import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import {
    Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, Stack, TextField, Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { useAuth } from '../../../auth/AuthProvider';
import { cancelAppointment, createRescheduledAppointment } from '../../../services/appointments';
import { useAgendaTranslation } from '../translations';

export const statusLabels = {
    SCHEDULED: 'scheduled', ONGOING: 'ongoing', COMPLETED: 'completed', CANCELLED: 'cancelled'
};
export default function AppointmentDialogs({ appointment, mode, zone, onClose }) {
    const { user, role } = useAuth();
    const actor = { user, role };
    const { t } = useAgendaTranslation();
    const client = useQueryClient();
    const active = useRef(true);
    const lock = useRef(false);
    const cancelled = useRef(false);
    const [originalCancelled, setOriginalCancelled] = useState(false);
    const [uncertain, setUncertain] = useState(false);
    const [verified, setVerified] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(false);
    const [time, setTime] = useState(dayjs(appointment.ts).tz(zone).format('YYYY-MM-DDTHH:mm'));
    const [duration, setDuration] = useState(appointment.slot_duration || 30);
    useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
    const parsed = time && dayjs(time).isValid() ? dayjs.tz(time, zone) : null;
    const valid = parsed?.isValid() && parsed.format('YYYY-MM-DDTHH:mm') === time && parsed.isAfter(dayjs())
        && Number.isInteger(Number(duration)) && Number(duration) > 0;
    const changed = parsed?.isValid() && (parsed.valueOf() !== dayjs(appointment.ts).valueOf()
        || Number(duration) !== Number(appointment.slot_duration));
    const invalidate = () => {
        if (active.current) void client.invalidateQueries({ queryKey: ['clinic-agenda-appointments'] });
    };
    const save = async () => {
        if (lock.current || (mode === 'reschedule' && (!valid || !changed || (uncertain && !verified)))) return;
        lock.current = true;
        setBusy(true); setError(false);
        try {
            if (!cancelled.current) {
                await cancelAppointment(actor, appointment);
                cancelled.current = true;
                if (!active.current) return;
                setOriginalCancelled(true);
                invalidate();
            }
            if (mode === 'reschedule') {
                try {
                    await createRescheduledAppointment(actor, appointment, {
                        ts: parsed.toISOString(), slot_duration: Number(duration)
                    });
                } catch (creationError) {
                    if (active.current) { setUncertain(true); setVerified(false); }
                    throw creationError;
                }
            }
            if (active.current) { invalidate(); onClose(); }
        } catch {
            if (active.current) { setError(true); invalidate(); }
        } finally {
            lock.current = false;
            if (active.current) setBusy(false);
        }
    };
    return <Dialog open fullWidth maxWidth="sm" onClose={busy ? undefined : onClose} aria-labelledby="appointment-action-title">
        <DialogTitle id="appointment-action-title">{t(mode === 'reschedule' ? 'agenda.reschedule' : 'Cancelar agendamento')}</DialogTitle>
        <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
                <Typography>{appointment.patient.name} · {appointment.doctor.name}</Typography>
                {error && <Alert severity="error">{t(originalCancelled && mode === 'reschedule'
                    ? 'agenda.reschedulePartialError' : 'agenda.saveError')}</Alert>}
                {mode === 'reschedule' ? <>
                    <Typography variant="body2" color="text.secondary">{t('O agendamento original será cancelado e será criado um novo no horário indicado.')}</Typography>
                    {originalCancelled && <Alert severity="warning">{t('O horário original já foi cancelado. Conclua a remarcação ou feche para a tratar posteriormente.')}</Alert>}
                    <TextField label={t('agenda.newTime')} type="datetime-local" value={time}
                        onChange={(event) => setTime(event.target.value)} disabled={busy}
                        InputLabelProps={{ shrink: true }} error={!valid} helperText={!valid ? t('agenda.futureTime') : undefined} />
                    <TextField label={t('agenda.duration')} type="number" value={duration}
                        onChange={(event) => setDuration(event.target.value)} disabled={busy}
                        inputProps={{ min: 1, step: 1 }} />
                    {uncertain && <FormControlLabel control={<Checkbox checked={verified} disabled={busy}
                        onChange={(event) => setVerified(event.target.checked)} />} label={t('agenda.verifiedNotCreated')} />}
                </> : <Typography>{t('O agendamento será cancelado.')}</Typography>}
            </Stack>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} disabled={busy}>{t('agenda.close')}</Button>
            <Button variant="contained" onClick={save}
                disabled={busy || (mode === 'reschedule' && (!valid || !changed || (uncertain && !verified)))}>
                {t(mode === 'reschedule' ? 'agenda.save' : 'Confirmar cancelamento')}
            </Button>
        </DialogActions>
    </Dialog>;
}
AppointmentDialogs.propTypes = {
    appointment: PropTypes.object.isRequired, mode: PropTypes.oneOf(['reschedule', 'cancel']).isRequired,
    zone: PropTypes.string.isRequired, onClose: PropTypes.func.isRequired
};
